// Lightweight DE/EN i18n. Dictionary-based + helper for content nodes that store {de,en}.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const dict = {
    de: {
        nav: {
            services: "Leistungen",
            industries: "Branchen",
            cases: "Projekte",
            faq: "FAQ",
            contact: "Kontakt",
            login: "Admin Login",
            client_login: "Kunden-Login",
            client_login_hint: "Zugang nur für bestehende Kunden",
        },
        cta: {
            whatsapp: "WhatsApp Anfrage starten",
            call: "Jetzt anrufen",
            analyse: "Jetzt kostenlose Beratung sichern",
            book: "Beratung sichern",
            explore: "Mehr erfahren",
        },
        sticky: {
            whatsapp: "WhatsApp",
            call: "Anrufen",
            analyse: "Beratung",
        },
        hero: {
            top: "Für Unternehmer · Mehr Kundenanfragen",
            trust: "Empfohlen von Unternehmern, die planbar wachsen wollen",
        },
        form: {
            title: "Kostenlose Beratung anfragen",
            subtitle:
                "Hinterlasse deine Daten. Wir melden uns binnen 24 Stunden bei dir.",
            step: "Schritt",
            of: "von",
            name: "Vor- und Nachname",
            company: "Firma",
            phone: "Telefon",
            email: "E-Mail",
            industry_q: "In welcher Branche bist du tätig?",
            need_q: "Was brauchst du?",
            message: "Was ist dein Ziel?",
            submit: "Beratung anfragen",
            next: "Weiter",
            back: "Zurück",
            sending: "Wird gesendet...",
            success: "Danke! Deine Anfrage ist eingegangen.",
            error: "Es ist ein Fehler aufgetreten.",
            placeholder_name: "z. B. Max Mustermann",
            placeholder_company: "z. B. Muster GmbH",
            placeholder_phone: "+49 ...",
            placeholder_email: "name@firma.de",
            placeholder_message:
                "Was möchtest du erreichen? (optional, max. 500 Zeichen)",
        },
        sections: {
            social_title:
                "Warum verlierst du jeden Tag potenzielle Kunden?",
            social_sub:
                "Veraltete Webseiten. Komplizierte Kontaktwege. Lange Antwortzeiten. Interessenten springen ab — bevor sie überhaupt eine Anfrage stellen. Wir lösen genau das.",
            services_title: "Was wir für dich umsetzen",
            services_sub:
                "Moderne Außendarstellung. Direkte Kontaktwege. Mehr echte Anfragen — ohne Streuverluste.",
            cases_eyebrow: "Erfolge aus der Praxis",
            cases_title: "Wie wir aus Webseiten Kundenanfragen machen",
            cases_sub:
                "Vier Branchen, vier Systeme. Beispiele, wie aus Klicks echte Anfragen werden — fokussiert auf das, was wirklich Umsatz bringt.",
            features_title: "Alles, was dein System bringt",
            features_sub:
                "Mehr Sichtbarkeit. Mehr Vertrauen. Mehr Kundenanfragen — messbar an einem Ort.",
            faq_title: "Häufige Fragen",
            faq_sub: "Klar, kurz, ehrlich.",
            cta_title: "Bereit für planbar mehr Kundenanfragen?",
            cta_sub:
                "Schreib uns auf WhatsApp oder sichere dir eine kostenlose Beratung.",
        },
        cookie: {
            title: "Cookies & Datenschutz",
            text: "Wir verwenden Cookies, um die Performance unserer Seite zu messen und Inhalte zu personalisieren. Du kannst frei entscheiden.",
            accept_all: "Alle akzeptieren",
            necessary: "Nur notwendige",
            settings: "Einstellungen",
            cat_necessary: "Notwendig",
            cat_stats: "Statistik",
            cat_marketing: "Marketing",
            save: "Auswahl speichern",
            necessary_locked: "Immer aktiv",
        },
        chat: {
            title: "AI Berater",
            subtitle: "Frag uns alles zu Lead-Funneln",
            placeholder: "Schreib eine Nachricht...",
            welcome:
                "Hi! Ich bin der KI-Berater von BA Media. Wie kann ich dir helfen — mehr Anfragen, Sichtbarkeit, Automatisierung?",
            send: "Senden",
            error: "Tut uns leid, ein Fehler ist passiert.",
        },
        admin: {
            login_title: "Admin Login",
            login_sub: "Sichere dir Zugang zum BA Media Performance Cockpit.",
            email: "E-Mail",
            password: "Passwort",
            login: "Einloggen",
            forgot: "Passwort vergessen?",
            back_home: "Zurück zur Website",
            logout: "Abmelden",
        },
        thanks: {
            title: "Danke. Deine Anfrage ist da.",
            sub: "Wir melden uns binnen 24 Stunden — und schreiben dir auch direkt auf WhatsApp, falls du es eilig hast.",
            cta_home: "Zur Startseite",
        },
        footer: {
            services: "Leistungen",
            company: "Unternehmen",
            legal: "Rechtliches",
            imprint: "Impressum",
            privacy: "Datenschutz",
            agb: "AGB",
            contact: "Kontakt",
        },
        common: {
            loading: "Lädt...",
            save: "Speichern",
            cancel: "Abbrechen",
        },
    },
    en: {
        nav: {
            services: "Services",
            industries: "Industries",
            cases: "Cases",
            faq: "FAQ",
            contact: "Contact",
            login: "Admin Login",
            client_login: "Client Login",
            client_login_hint: "Access for existing clients only",
        },
        cta: {
            whatsapp: "Start WhatsApp inquiry",
            call: "Call now",
            analyse: "Book a free consultation",
            book: "Book consultation",
            explore: "Learn more",
        },
        sticky: {
            whatsapp: "WhatsApp",
            call: "Call",
            analyse: "Consult",
        },
        hero: {
            top: "For Business Owners · More Customer Inquiries",
            trust: "Recommended by business owners who want predictable growth",
        },
        form: {
            title: "Book a free consultation",
            subtitle:
                "Leave your details. We'll get back to you within 24 hours.",
            step: "Step",
            of: "of",
            name: "First and last name",
            company: "Company",
            phone: "Phone",
            email: "Email",
            industry_q: "Which industry are you in?",
            need_q: "What do you need?",
            message: "What's your goal?",
            submit: "Request consultation",
            next: "Next",
            back: "Back",
            sending: "Sending...",
            success: "Thanks! Your request is in.",
            error: "Something went wrong.",
            placeholder_name: "e.g. John Doe",
            placeholder_company: "e.g. Acme Inc.",
            placeholder_phone: "+49 ...",
            placeholder_email: "name@company.com",
            placeholder_message:
                "What do you want to achieve? (optional, max 500 chars)",
        },
        sections: {
            social_title:
                "Why you lose potential customers every day",
            social_sub:
                "Outdated websites. Complex contact paths. Slow response times. Prospects leave — before they even make an inquiry. That's exactly what we fix.",
            services_title: "What we deliver for you",
            services_sub: "Modern brand. Direct contact paths. More real inquiries — without wasted spend.",
            cases_eyebrow: "Real results",
            cases_title: "How we turn websites into customer inquiries",
            cases_sub: "Four industries, four systems. Real examples of how clicks become inquiries — focused on what actually drives revenue.",
            features_title: "Everything your system delivers",
            features_sub:
                "More visibility. More trust. More real inquiries — measurable in one place.",
            faq_title: "Frequently Asked Questions",
            faq_sub: "Clear, short, honest.",
            cta_title: "Ready for predictable customer inquiries?",
            cta_sub: "Message us on WhatsApp or book a free consultation.",
        },
        cookie: {
            title: "Cookies & privacy",
            text: "We use cookies to measure performance and personalize content. You decide.",
            accept_all: "Accept all",
            necessary: "Necessary only",
            settings: "Settings",
            cat_necessary: "Necessary",
            cat_stats: "Statistics",
            cat_marketing: "Marketing",
            save: "Save selection",
            necessary_locked: "Always on",
        },
        chat: {
            title: "AI Advisor",
            subtitle: "Ask anything about lead funnels",
            placeholder: "Type a message...",
            welcome:
                "Hi! I'm BA Media's AI advisor. How can I help — more inquiries, visibility, automation?",
            send: "Send",
            error: "Sorry, something went wrong.",
        },
        admin: {
            login_title: "Admin Login",
            login_sub: "Secure access to the BA Media Performance cockpit.",
            email: "Email",
            password: "Password",
            login: "Sign in",
            forgot: "Forgot password?",
            back_home: "Back to website",
            logout: "Log out",
        },
        thanks: {
            title: "Thanks. We got it.",
            sub: "We'll get back within 24 hours — feel free to message us on WhatsApp if it's urgent.",
            cta_home: "Back to homepage",
        },
        footer: {
            services: "Services",
            company: "Company",
            legal: "Legal",
            imprint: "Imprint",
            privacy: "Privacy",
            agb: "Terms",
            contact: "Contact",
        },
        common: { loading: "Loading...", save: "Save", cancel: "Cancel" },
    },
};

const I18nContext = createContext({
    locale: "de",
    setLocale: () => {},
    t: (k) => k,
    pick: (n) => "",
});

export function I18nProvider({ children }) {
    const [locale, setLocale] = useState(() => {
        return localStorage.getItem("bm_locale") || "de";
    });

    useEffect(() => {
        localStorage.setItem("bm_locale", locale);
        document.documentElement.lang = locale;
    }, [locale]);

    const t = useCallback(
        (path) => {
            const parts = path.split(".");
            let cur = dict[locale];
            for (const p of parts) {
                if (!cur) return path;
                cur = cur[p];
            }
            return cur ?? path;
        },
        [locale],
    );

    const pick = useCallback(
        (node) => {
            if (!node) return "";
            if (typeof node === "string") return node;
            if (typeof node === "object") return node[locale] ?? node.de ?? node.en ?? "";
            return String(node);
        },
        [locale],
    );

    const value = useMemo(() => ({ locale, setLocale, t, pick }), [locale, t, pick]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}
